import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { PublicLayout } from '@/components/public/PublicLayout';
import { SilaHero } from '@/components/public/SilaHero';
import { Seo } from '@/components/Seo';
import { mediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { RentalBookingDialog } from '@/components/public/RentalBookingDialog';
import { rentalsService } from '@/services/rentals.service';
import { useLivePageContent } from '@/hooks/usePageContent';
import { ConciergeBell, MapPin, Search, Store, Shuffle, ArrowRight, PackageX, Wallet, CalendarRange, Compass } from 'lucide-react';

const SERVIZI_HERO_IMG = 'https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600';
const str = (v) => (typeof v === 'string' || typeof v === 'number' ? String(v) : '');

const euro = (n) => `€${Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const reveal = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
};

function ServiceMedallion({ s, onOpen }) {
  const priceLabel = s.pricing_mode === 'per_day' ? `${euro(s.price)}/g` : euro(s.price);
  return (
    <motion.div {...reveal} className="group flex flex-col items-center text-center" data-testid={`noleggi-card-${s.slug}`}>
      {/* Medaglione: foto circolare protagonista */}
      <button
        type="button"
        onClick={() => onOpen(s)}
        className="relative rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        aria-label={`Apri ${s.title}`}
      >
        <div className="relative h-40 w-40 overflow-hidden rounded-full bg-muted shadow-md ring-1 ring-border transition-[transform,box-shadow] duration-300 group-hover:-translate-y-1 group-hover:shadow-xl group-hover:ring-2 group-hover:ring-primary/60 sm:h-44 sm:w-44">
          {s.cover ? (
            <img src={mediaUrl(s.cover)} alt={s.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
          ) : (
            <div className="flex h-full w-full items-center justify-center"><ConciergeBell className="h-10 w-10 text-muted-foreground" /></div>
          )}
          <div className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(circle at 50% 120%, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 55%)' }} />
        </div>
        {/* Prezzo: disco flottante */}
        <span className="absolute -right-1 top-2 flex h-14 w-14 flex-col items-center justify-center rounded-full border-2 border-background bg-primary text-primary-foreground shadow-lg">
          <span className="text-sm font-bold leading-none tabular-nums">{euro(s.price)}</span>
          {s.pricing_mode === 'per_day' && <span className="mt-0.5 text-[9px] font-medium uppercase tracking-wide opacity-90">/giorno</span>}
        </span>
        {/* Categoria: pill sul bordo inferiore */}
        <span className="absolute -bottom-2 left-1/2 max-w-[90%] -translate-x-1/2 truncate whitespace-nowrap rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-foreground shadow-sm">
          {s.category_label}
        </span>
      </button>

      {/* Testo sotto il medaglione */}
      <div className="mt-5 flex w-full flex-col items-center px-1">
        <h3 className="font-display text-base font-semibold leading-snug text-foreground line-clamp-1">{s.title}</h3>
        <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground line-clamp-1"><MapPin className="h-3.5 w-3.5 text-primary" />{s.location || 'Sila'}</p>
        <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground line-clamp-1"><Store className="h-3 w-3" />{s.owner_name || 'Operatore Sila'}</p>
        <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          {s.pricing_mode === 'per_day'
            ? <span className="flex items-center gap-1"><CalendarRange className="h-3 w-3" />al giorno</span>
            : <span className="flex items-center gap-1"><CalendarRange className="h-3 w-3" />prezzo fisso</span>}
          {s.deposit > 0 && <span className="flex items-center gap-1"><Wallet className="h-3 w-3" />cauzione {euro(s.deposit)}</span>}
        </div>
        <Button size="sm" className="mt-3 rounded-full px-5" onClick={() => onOpen(s)} data-testid={`noleggi-book-${s.slug}`}>
          Prenota · {priceLabel} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      </div>
    </motion.div>
  );
}

export default function Servizi() {
  const [loading, setLoading] = useState(true);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState('tutte');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async (cat) => {
    setLoading(true);
    try {
      const data = await rentalsService.publicList(cat);
      setServices(data?.services || []);
      setCategories(data?.categories || []);
    } catch (e) {
      toast.error('Impossibile caricare i servizi. Riprova.');
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(activeCat); }, [activeCat, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => (s.title || '').toLowerCase().includes(q) || (s.location || '').toLowerCase().includes(q) || (s.category_label || '').toLowerCase().includes(q));
  }, [services, query]);

  const openDialog = (s) => { setSelected(s); setDialogOpen(true); };

  const ov = useLivePageContent('servizi');

  return (
    <PublicLayout>
      <Seo title="Servizi — Sila Experiences" description="Servizi e forniture per il tuo soggiorno in Sila: auto a noleggio, navetta, energia rinnovabile, assicurazioni, internet satellitare, legna e gas. Prenoti online, l'operatore ti assiste." />

      <SilaHero
        image={str(ov.heroImage) || SERVIZI_HERO_IMG}
        badge={str(ov.eyebrow) || 'Servizi'}
        title={str(ov.title) || 'Tutti i servizi per la tua Sila'}
        subtitle={str(ov.lead) || "Auto a noleggio, navetta, energia, internet satellitare, legna, gas e molto altro dai migliori operatori del territorio. Prenoti online, l'operatore pensa al resto."}
        icon={ConciergeBell}
        minH="60vh"
        testId="noleggi-hero"
      >
        <div className="flex flex-wrap items-center gap-3">
          <Button size="lg" className="rounded-full px-6 text-sm font-semibold" onClick={() => document.getElementById('noleggi-content')?.scrollIntoView({ behavior: 'smooth' })} data-testid="noleggi-hero-cta">
            <ConciergeBell className="mr-2 h-4 w-4" /> Esplora i servizi
          </Button>
        </div>
      </SilaHero>

      <div id="noleggi-content" className="sila-section py-10 sm:py-12">
        {/* BARRA FILTRI STICKY */}
        <div className="sticky top-[72px] z-30 -mx-4 px-4 sm:mx-0 sm:px-0">
          <div className="rounded-2xl border border-border/70 bg-background/80 p-3 shadow-sm backdrop-blur-md">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 lg:pb-0" data-testid="noleggi-category-pill-group">
                <button type="button" onClick={() => setActiveCat('tutte')} data-testid="noleggi-category-pill-tutte"
                  className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${activeCat === 'tutte' ? 'border-transparent bg-primary text-primary-foreground shadow-sm' : 'border-border bg-card/70 text-muted-foreground hover:bg-accent/50'}`}>
                  Tutti
                </button>
                {categories.map((c) => {
                  const active = activeCat === c.slug;
                  return (
                    <button key={c.slug} type="button" onClick={() => setActiveCat(c.slug)} data-testid={`noleggi-category-pill-${c.slug}`}
                      className={`shrink-0 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${active ? 'border-transparent bg-primary text-primary-foreground shadow-sm' : 'border-border bg-card/70 text-muted-foreground hover:bg-accent/50'}`}>
                      {c.label}
                    </button>
                  );
                })}
              </div>
              <div className="relative lg:w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca servizio o località" className="pl-9" data-testid="noleggi-search-input" />
              </div>
            </div>
          </div>
        </div>

        {/* Nota anti-monopolio + cross-link a Esperienze */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="noleggi-fairness-note">
            <Shuffle className="h-3.5 w-3.5 text-primary" />
            <span>Servizi mostrati in ordine <strong className="text-foreground">casuale ed equo</strong> tra tutti gli operatori.</span>
          </div>
          <Link to="/esperienze" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card/70 px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent/50" data-testid="noleggi-crosslink-esperienze">
            <Compass className="h-3.5 w-3.5 text-primary" /> Cerchi noleggi bici, quad o escursioni? Vai a Esperienze
          </Link>
        </div>

        {/* GRID MEDAGLIONI */}
        <div className="mt-6">
          {loading ? (
            <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-4" data-testid="noleggi-loading">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="flex flex-col items-center gap-3">
                  <Skeleton className="h-40 w-40 rounded-full sm:h-44 sm:w-44" />
                  <Skeleton className="h-4 w-28 rounded" />
                  <Skeleton className="h-3 w-20 rounded" />
                  <Skeleton className="h-8 w-28 rounded-full" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center rounded-2xl border border-dashed border-border py-16 text-center" data-testid="noleggi-empty">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-muted"><PackageX className="h-7 w-7 text-muted-foreground" /></div>
              <p className="font-medium text-foreground">Nessun servizio disponibile</p>
              <p className="text-sm text-muted-foreground">Prova a cambiare categoria o ricerca.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 lg:grid-cols-4" data-testid="noleggi-grid">
              {filtered.map((s) => <ServiceMedallion key={s.id} s={s} onOpen={openDialog} />)}
            </div>
          )}
        </div>
      </div>

      <RentalBookingDialog service={selected} open={dialogOpen} onClose={() => { setDialogOpen(false); load(activeCat); }} />
    </PublicLayout>
  );
}
